
import React from 'react';
import { useTranslationEdit } from '@/contexts/TranslationEditContext';
import { Input } from '@/components/ui/input';
import { Pencil } from 'lucide-react';

interface TranslationInputProps extends React.ComponentPropsWithoutRef<typeof Input> {
  i18nKey: string;
  placeholderText: string;
  values?: Record<string, string | number>;
}

export function TranslationInput({ 
  i18nKey, 
  placeholderText, 
  className,
  disabled,
  onClick,
  onChange,
  values,
  ...props 
}: TranslationInputProps) {
  const { isEditMode, setEditingKey } = useTranslationEdit();
  
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (isEditMode) {
      e.preventDefault();
      e.stopPropagation();
      setEditingKey(i18nKey);
      return;
    }
    
    if (onClick) {
      onClick(e);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isEditMode && onChange) {
      onChange(e);
    }
  };
  
  // In edit mode, we should never actually disable the input
  // but we can show it as visually disabled if it would normally be disabled
  const isDisabled = disabled && !isEditMode;
  const visuallyDisabled = disabled && isEditMode;
  
  return (
    <div className="relative group w-full">
      <Input
        {...props}
        placeholder={placeholderText}
        className={`${className || ''} ${isEditMode ? 'translation-editable cursor-pointer' : ''} ${visuallyDisabled ? 'opacity-50' : ''}`}
        data-i18n-key={isEditMode ? i18nKey : undefined}
        disabled={isDisabled}
        onClick={handleClick}
        onChange={handleChange}
        readOnly={isEditMode}
      />
      {isEditMode && (
        <div 
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            setEditingKey(i18nKey);
          }}
        >
          <Pencil className="h-4 w-4 text-primary" />
        </div>
      )}
    </div>
  );
}
